import { type RefCallback, useEffect, useState } from "react";

const isTabbable = (element: HTMLElement) =>
	element.tabIndex >= 0 &&
	!element.closest("[hidden], [inert]") &&
	!(element as HTMLButtonElement).disabled &&
	(element as HTMLInputElement).type !== "hidden";

/** What Tab reaches inside a container, in the order it reaches them. */
const tabbables = (container: HTMLElement): HTMLElement[] =>
	[...container.querySelectorAll<HTMLElement>("*")].filter(isTabbable);

/**
 * How many controls Tab reaches before an element in its container. An element
 * outside the tab order (tabIndex -1) still has a place in it this way: before
 * the next control Tab reaches.
 */
const tabOrderPlace = (container: HTMLElement, element: HTMLElement) => {
	let place = 0;
	for (const candidate of container.querySelectorAll<HTMLElement>("*")) {
		if (candidate === element) break;
		if (isTabbable(candidate)) place += 1;
	}
	return place;
};

const replacements = new WeakMap<HTMLElement, HTMLElement>();

/**
 * The control that took a removed one's place in a container kept by
 * {@link useKeepFocus}, while it is still on the page. A dialog opened from the
 * removed control gives focus to this one when it closes.
 */
export const replacementFor = (
	removed: HTMLElement
): HTMLElement | undefined => {
	const replacement = replacements.get(removed);
	return replacement?.isConnected ? replacement : undefined;
};

/**
 * The control in a container that last held focus, where it stood in the tab
 * order, and whether focus is still on it. Focus leaving for somewhere else,
 * a dialog included, is not a removal: the control is then only remembered,
 * so its replacement can be found. A removal blurs to nowhere and leaves the
 * control detached.
 */
type Held = { element: HTMLElement; index: number; focused: boolean };

const isLost = (container: HTMLElement) => {
	const active = document.activeElement;
	return !active || active === document.body || active === container;
};

/** Watches one container; returns what stops watching. */
function keepFocusInside(container: HTMLElement): () => void {
	let held: Held | null = null;

	const hold = (element: EventTarget | null) => {
		if (!(element instanceof HTMLElement)) return;
		if (element === container || !container.contains(element)) return;
		held = { element, index: tabOrderPlace(container, element), focused: true };
	};

	const onFocusOut = (event: FocusEvent) => {
		const left = held;
		if (left?.element !== event.target) return;
		const to = event.relatedTarget;
		if (to instanceof Node) {
			left.focused = container.contains(to);
			return;
		}
		queueMicrotask(() => {
			left.focused = !left.element.isConnected;
		});
	};

	const onRemoved = () => {
		if (!held) return;
		// Rows come and go around the held control too; its place moves with them.
		if (held.element.isConnected) {
			held.index = tabOrderPlace(container, held.element);
			return;
		}
		const { element, index, focused } = held;
		held = null;
		const remaining = tabbables(container);
		const replacement =
			remaining[Math.min(index, remaining.length - 1)] ??
			(container.hasAttribute("tabindex") ? container : undefined);
		if (replacement) replacements.set(element, replacement);
		if (focused && isLost(container)) replacement?.focus();
	};

	const onFocusIn = (event: FocusEvent) => hold(event.target);
	const observer = new MutationObserver(onRemoved);
	// A dialog focuses its first control before anything subscribes.
	hold(document.activeElement);
	container.addEventListener("focusin", onFocusIn);
	container.addEventListener("focusout", onFocusOut);
	observer.observe(container, { childList: true, subtree: true });
	return () => {
		container.removeEventListener("focusin", onFocusIn);
		container.removeEventListener("focusout", onFocusOut);
		observer.disconnect();
	};
}

/**
 * Keeps keyboard focus inside a container when the control holding it is
 * removed, as when a list is read again without the row somebody just removed.
 * Focus moves to the control that took its place in the tab order, to the last
 * one when it was the last, or to the container itself when none is left and
 * the container takes focus. Without it focus falls to the page, and a
 * keyboard user starts again from the top.
 *
 * When focus is away in a dialog the control opened, as it is while a
 * confirmation closes, nothing moves then: the replacement is recorded for
 * {@link replacementFor}, and the dialog gives focus to it as it closes.
 *
 * Radix's modal dialogs try the same and miss: their focus scope subscribes
 * again whenever it re-renders, and when that lands in the same commit as the
 * removal the mutation it was waiting for is dropped
 * (https://github.com/radix-ui/primitives/blob/main/packages/react/focus-scope/src/focus-scope.tsx).
 * So the subscription here lives in an effect keyed on the element, which a
 * re-render leaves alone, and not in the ref callback, which Radix's composed
 * refs call again on every render.
 */
export function useKeepFocus<T extends HTMLElement>(): RefCallback<T> {
	const [container, setContainer] = useState<T | null>(null);
	useEffect(
		() => (container ? keepFocusInside(container) : undefined),
		[container]
	);
	return setContainer;
}
